import { get } from 'vuex-pathify';

export default {
  computed: {
    mapPanelDataFetched: get('map/mapPanelDataFetched'),
    permissions: get('users/toolsPermissions'),
    settings: get('admin/settingsValues'),
    additionalModules: get('admin/additionalModules'),
    isIdentificationToolActive: get('tools/toolStatus@isIdentificationToolActive'),
    isParcelReportToolActive: get('tools/toolStatus@isParcelReportToolActive'),
    isReverseGeocoderToolActive: get('tools/toolStatus@isReverseGeocoderToolActive'),
    isCoordinatesToolActive: get('tools/toolStatus@isCoordinatesToolActive'),
    isMeasurementsToolActive: get('tools/toolStatus@isMeasurementsToolActive'),
    isStreetViewToolActive: get('tools/toolStatus@isStreetViewToolActive'),
    activeTool: get('tools/activeTool'),
    isNmtConfigured: get('admin/additionalModules@DEM.configured'),
    isSelectionActive: get('tools/toolStatus@isSelectionActive'),
    parcelsProvider: get('admin/settingsValues@parcels_provider'),
    projectElements: get('layers/project@layers'),
    parcelsMapping: get('admin/modulesMapping@parcels'),
    addressesMapping: get('admin/modulesMapping@addresses'),
    modulesMapping: get('admin/modulesMapping'),
    isActiveToolNotReplaceable: get('tools/isActiveToolNotReplaceable'),
    isSearchParcelsConfigured() {
      if (this.settings?.parcels_provider === 'uldk') {
        return true;
      } else {
        const elements = ['attribute_community', 'attribute_region', 'attribute_number', 'parcels_datasource'];
        return elements.every(element => this.parcelsMapping[element]);
      }
    },
    isSearchAddressesConfigured() {
      if (this.settings?.addresses_provider === 'gugik_api') {
        return true;
      } else {
        const elements = [
          'attribute_place',
          'attribute_postal_code',
          'attribute_street',
          'attribute_number',
          'addresses_datasource',
        ];
        return elements.every(element => this.addressesMapping[element]);
      }
    },
    isNextViewDisabled: get('tools/isNextViewDisabled'),
    isPreviousViewDisabled: get('tools/isPreviousViewDisabled'),
    tools() {
      return [
        {
          icon: 'mdi-cursor-default-click',
          name: 'identification',
          isAuthorized: false,
          hasState: true,
          hasCustomAction: true,
          disabled:
            !this.mapPanelDataFetched ||
            (this.isActiveToolNotReplaceable && this.activeTool !== 'moduleIdentification'),
          isActive: this.isIdentificationToolActive,
          actionParams: {
            layersFilter: layer => layer.get('isSpecial') === false,
          },
          tooltip: this.mapPanelDataFetched
            ? this.isActiveToolNotReplaceable
              ? this.$i18n.t('button.oneToolActive')
              : this.$i18n.t('navbar.identification', {
                  prefix: this.$i18n.t(`navbar.${this.isIdentificationToolActive ? 'off' : 'on'}`),
                })
            : this.$i18n.t('default.downloadingDataInProgress'),
        },
        {
          icon: '$coordinates',
          name: 'coordinates',
          hasState: true,
          isActive: this.isCoordinatesToolActive,
          disabled: this.isActiveToolNotReplaceable,
          isAuthorized: true,
          enableModuleName: ['COORDINATES'],
          hasCustomAction: false,
          color: 'font',
          tooltip: this.isActiveToolNotReplaceable
            ? this.$i18n.t('button.oneToolActive')
            : this.$i18n.t('navbar.coordinates', {
                prefix: this.$i18n.t(`navbar.${this.isCoordinatesToolActive ? 'off' : 'on'}`),
              }),
        },
        {
          fontSize: '19px',
          buttonSize: 'small',
          icon: 'mdi-selection-marker',
          name: 'locationSelection',
          isAuthorized: true,
          hasCustomAction: false,
          enableModuleName: ['SELECT_BY_LOCATION_MODULE'],
          tooltip: this.$i18n.t('navbar.locationSelection'),
        },
        {
          fontSize: '14px',
          buttonSize: 'small',
          icon: 'DXF',
          name: 'dxf',
          routeName: 'dxf',
          isAuthorized: true,
          isToolWorking: true,
          hasCustomAction: false,
          enableModuleName: ['ZDM_DATA'],
          tooltip: this.$i18n.t('navbar.importDxf'),
        },
        {
          fontSize: '19px',
          buttonSize: 'small',
          icon: 'mdi-ruler',
          name: 'measurements',
          isAuthorized: false,
          hasState: true,
          disabled: this.isActiveToolNotReplaceable,
          hasDivider: true,
          isActive: this.isMeasurementsToolActive,
          tooltip: this.isActiveToolNotReplaceable
            ? this.$i18n.t('button.oneToolActive')
            : this.$i18n.t('navbar.measurements', {
                prefix: this.$i18n.t(`navbar.${this.isMeasurementsToolActive ? 'off' : 'on'}`),
              }),
        },
        {
          icon: 'mdi-plus-box',
          name: 'zoomIn',
          isAuthorized: false,
          tooltip: this.$i18n.t('navbar.zoomIn'),
        },
        {
          icon: 'mdi-minus-box',
          name: 'zoomOut',
          isAuthorized: false,
          tooltip: this.$i18n.t('navbar.zoomOut'),
        },
        {
          icon: 'mdi-fullscreen',
          name: 'defaultView',
          isAuthorized: false,
          tooltip: this.$i18n.t('navbar.defaultView'),
        },
        {
          icon: 'mdi-skip-previous',
          name: 'previuosView',
          isAuthorized: false,
          disabled: this.isPreviousViewDisabled,
          tooltip: this.isPreviousViewDisabled
            ? this.$i18n.t('navbar.noPreviousView')
            : this.$i18n.t('navbar.previuosView'),
        },
        {
          icon: 'mdi-skip-next',
          name: 'nextView',
          isAuthorized: false,
          hasDivider: true,
          disabled: this.isNextViewDisabled,
          tooltip: this.isNextViewDisabled ? this.$i18n.t('navbar.noNextView') : this.$i18n.t('navbar.nextView'),
        },
        {
          icon: 'mdi-printer',
          name: 'prints',
          routeName: 'prints',
          isAuthorized: true,
          enableModuleName: ['MAP_PRINT'],
          hasState: false,
          hasCustomAction: false,
          tooltip: this.$i18n.t('navbar.print'),
        },
        {
          icon: '$quickprint',
          name: 'quickprint',
          tooltip: this.$i18n.t('navbar.quickPrint'),
          fontSize: '24px',
          isAuthorized: true,
          enableModuleName: ['QUICK_PRINT_MODULE'],
          enableSettingName: ['quick_print_module_enabled'],
        },
        {
          icon: 'mdi-google-street-view',
          name: 'streetview',
          isAuthorized: true,
          enableModuleName: ['STREET_VIEW'],
          hasState: true,
          disabled: this.isActiveToolNotReplaceable,
          isActive: this.isStreetViewToolActive,
          tooltip: this.isActiveToolNotReplaceable
            ? this.$i18n.t('button.oneToolActive')
            : this.$i18n.t('navbar.streetview', {
                prefix: this.$i18n.t(`navbar.${this.isStreetViewToolActive ? 'off' : 'on'}`),
              }),
        },
        {
          icon: 'mdi-chart-areaspline',
          name: 'nmt',
          routeName: 'nmtMap',
          isAuthorized: true,
          hasState: false,
          hasDivider: true,
          hasCustomAction: false,
          enableModuleName: ['DEM'],
          enableSettingName: ['dem_module_enabled'],
          disabled: !this.isNmtConfigured,
          tooltip: this.isNmtConfigured
            ? this.$i18n.t('admin.nmt')
            : this.$i18n.t('snackbar.modulesNotConfigurate.NMT'),
        },
        {
          fontSize: '19px',
          buttonSize: 'small',
          icon: 'mdi-layers-search',
          name: 'searchParcel',
          routeName: 'uldk',
          isAuthorized: true,
          isToolWorking: true,
          hasCustomAction: this.parcelsProvider !== 'uldk',
          enableModuleName: ['PARCELS'],
          enableSettingName: ['parcels_module_enabled'],
          disabled: !this.isSearchParcelsConfigured,
          tooltip: this.isSearchParcelsConfigured
            ? this.$i18n.t('admin.parcelsSearch')
            : this.$i18n.t('snackbar.modulesNotConfigurate.searchParcels'),
        },
        {
          fontSize: '19px',
          buttonSize: 'small',
          icon: 'mdi-home-search',
          name: 'searchAddress',
          isAuthorized: true,
          hasDivider: true,
          isToolWorking: true,
          enableModuleName: ['ADDRESSES'],
          enableSettingName: ['addresses_module_enabled'],
          disabled: !this.isSearchAddressesConfigured,
          tooltip: this.isSearchAddressesConfigured
            ? this.$i18n.t('admin.addressesSearch')
            : this.$i18n.t('snackbar.modulesNotConfigurate.searchAddresses'),
        },
        {
          fontSize: '24px',
          icon: '$parcelReport',
          name: 'parcelReport',
          isActive: this.isParcelReportToolActive,
          isAuthorized: true,
          enableModuleName: ['PARCELS_REPORT', 'PARCELS'],
          enableSettingName: ['parcel_report_module_enabled', 'parcels_module_enabled'],
          enableLogic: 'ALL',
          hasState: true,
          hasCustomAction: true,
          disabled: !this.isSearchParcelsConfigured || this.isActiveToolNotReplaceable,
          tooltip: this.isActiveToolNotReplaceable
            ? this.$i18n.t('button.oneToolActive')
            : !this.isSearchParcelsConfigured
            ? this.$i18n.t('snackbar.modulesNotConfigurate.searchParcels')
            : this.$i18n.t('navbar.parcelReport'),
        },
        {
          fontSize: '19px',
          buttonSize: 'small',
          icon: '$reverseGeocoder',
          name: 'reverseGeocoder',
          enableModuleName: ['ADDRESSES', 'PARCELS'],
          enableSettingName: ['addresses_module_enabled', 'parcels_module_enabled'],
          enableLogic: 'ALL',
          isAuthorized: true,
          isActive: this.isReverseGeocoderToolActive,
          hasState: true,
          tooltip: !this.isSearchAddressesConfigured
            ? this.$i18n.t('snackbar.modulesNotConfigurate.searchAddresses')
            : !this.isSearchParcelsConfigured
            ? this.$i18n.t('snackbar.modulesNotConfigurate.searchParcels')
            : this.$i18n.t('admin.reverseGeocoderSearch'),
          hasCustomAction: true,
          disabled:
            !this.isSearchAddressesConfigured || !this.isSearchParcelsConfigured || this.isActiveToolNotReplaceable,
        },
        {
          icon: 'mdi-car',
          name: 'VEHICLES',
          isAuthorized: true,
          hasCustomAction: false,
          enableModuleName: ['VEHICLES'],
          enableSettingName: ['vehicles_module_enabled'],
          moduleLayerTooltipPath: 'navbar.toggleVehicles',
          moduleLayerId: this.modulesMapping?.vehicles?.layer_id,
        },
        {
          icon: 'mdi-cellphone',
          name: 'MOBILE',
          isAuthorized: true,
          isToolWorking: true,
          enableModuleName: [this.getMobileName()],
          tooltip: this.$i18n.t('navbar.MOBILE'),
        },
        {
          icon: '$qgis',
          name: 'QGIS_PLUGIN',
          isAuthorized: true,
          isToolWorking: true,
          hasDivider:
            this.additionalModules['DOCUMENTATION']?.enabled ||
            this.additionalModules['HISTORY']?.enabled ||
            this.additionalModules['ZDM_STREETS']?.enabled,
          enableModuleName: ['QGIS_PLUGIN'],
          color: 'font',
          tooltip: this.$i18n.t('navbar.QGIS_PLUGIN'),
          fontSize: '19px',
        },
        {
          icon: 'mdi-text-box-plus',
          name: 'addDocument',
          isAuthorized: true,
          isToolWorking: true,
          hasDivider: this.additionalModules['DOCUMENTATION']?.enabled && !this.additionalModules['HISTORY']?.enabled,
          enableModuleName: ['DOCUMENTATION'],
          enableSettingName: ['documentation_module_enabled'],
          tooltip: this.$i18n.t('navbar.addNewDocument'),
          fontSize: '19px',
        },
        {
          icon: 'mdi-clipboard-text-clock',
          name: 'HISTORY',
          isAuthorized: true,
          hasCustomAction: false,
          hasDivider: this.additionalModules['HISTORY']?.enabled && this.additionalModules['ZDM_STREETS']?.enabled,
          enableModuleName: ['HISTORY'],
          tooltip: this.$i18n.t('navbar.historyList'),
          moduleLayerId: this.modulesMapping?.history?.history_layer_id,
        },
        {
          name: 'zdmStreets',
          isAuthorized: true,
          isToolWorking: true,
          hasDivider: false,
          enableModuleName: ['ZDM_STREETS'],
        },
      ];
    },
    filteredTools() {
      return this.tools.filter(tool => {
        if (!tool.isAuthorized) {
          return true;
        }
        if (!tool.enableLogic || tool.enableLogic === 'ALL') {
          const hasPermission = tool.enableModuleName
            ? tool.enableModuleName.find(name => !this.permissions[name]?.main_value > 0)
              ? false
              : true
            : true;
          const isEnabled = tool.enableSettingName
            ? tool.enableSettingName.find(name => !this.settings[name])
              ? false
              : true
            : true;
          const areAdditionalModulesEnabled = tool.enableModuleName.find(name => !this.additionalModules[name]?.enabled)
            ? false
            : true;
          return isEnabled && hasPermission && areAdditionalModulesEnabled;
        } else if (tool.enableLogic === 'ANY') {
          const hasPermission = tool.enableModuleName
            ? tool.enableModuleName.find(name => this.permissions[name]?.main_value > 0)
              ? true
              : false
            : true;
          const isEnabled = tool.enableSettingName
            ? tool.enableSettingName.find(name => this.settings[name])
              ? true
              : false
            : true;
          const areAdditionalModulesEnabled = tool.enableModuleName.find(name => this.additionalModules[name]?.enabled)
            ? true
            : false;
          return isEnabled && hasPermission && areAdditionalModulesEnabled;
        }
      });
    },
  },
  methods: {
    getMobileName() {
      if (process.env.VUE_APP_APP_KIND === 'zdm') {
        return 'ZDM_MOBILE';
      } else if (process.env.VUE_APP_APP_KIND === 'aquagis') {
        return 'AQUAGIS_MOBILE';
      } else if (process.env.VUE_APP_APP_KIND === 'aquagis_offline') {
        return 'AQUAGIS_OFFLINE_MOBILE';
      }
    },
  },
};
